import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { MdCheckCircle, MdWarning, MdInfo, MdErrorOutline, MdLayers } from 'react-icons/md';

import Loading from '../../components/Loading';
import { FieldContainer, Text, Input, Button } from '../../components';
import Code from '../../dtos/Code';
import { CodeBox, Code as CodeLabel } from '../../pages/Code/styles';
import { Select } from '../Select';
import answerRepository, { CreateQuestions } from '../../lib/answer';
import interactionRepository from '../../lib/interaction';
import ResourceDTO from '../../dtos/Resource';
import getPosition from '../../services/getPosition';
import codesRepository from '../../lib/codes';
import resourcesRepository from '../../lib/resource';

import {
  Body,
  ProductDetailsContainer,
  ProductLayout,
  ProductInfoContainer,
  ProductName,
  ProductDescription,
  Divider,
  StatsContainer,
  StatItem,
  StatLabel,
  StatValue,
  QuestionsCard,
  QuestionsTitle,
  QuestionScroll,
  ButtonContainer,
  SubmittedMessage,
  NoInteractionMessage,
  ExtraviadoAlert,
  AggregatedItemsCard,
  AggregatedTitle,
  AggregatedList,
  AggregatedItem,
} from './styles';

import StatusBadge from '../StatusBadge';

interface ProductByCodeProps {
  qrCode: Code;
  loadingResources: boolean;
  resource: ResourceDTO | undefined;
}

type ForumEntry = CreateQuestions;

const ProductByCode: React.FC<ProductByCodeProps> = ({
  qrCode,
  loadingResources,
  resource,
}) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [forum, setForum] = useState<ForumEntry[]>([]);
  const [latLng, setLatLng] = useState<{ lat: number; lng: number } | null>(null);
  const [aggregatedChildren, setAggregatedChildren] = useState<{code: string, resource: ResourceDTO}[]>([]);
  const [loadingAggregation, setLoadingAggregation] = useState(false);

  useEffect(() => {
    getPosition().then((pos) => {
      if (pos?.latitude && pos?.longitude) {
        setLatLng({ lat: pos.latitude, lng: pos.longitude });
      }
    });
  }, []);

  const changeValue = useCallback((questionId: string, value: string): void => {
    setForum((prev) => {
      const existing = prev.find((q) => q.questionId === questionId);
      if (existing) {
        return prev.map((q) =>
          q.questionId === questionId ? { ...q, answerText: value } : q
        );
      }
      return [
        ...prev,
        { questionId, answerText: value, code: qrCode.value, status: 'Envasado' },
      ];
    });
  }, [qrCode.value]);

  useEffect(() => {
    if (qrCode?.value === 'ADBFK1TKYQ') {
      const childCodes = ['ADUVBVRCYM', 'AD79GJMUKV', 'ADKRJEMSMB'];
      setLoadingAggregation(true);
      
      const fetchChildren = async () => {
        try {
          const results = [];
          for (const cCode of childCodes) {
            try {
              const codeData = await codesRepository.find(cCode);
              if (codeData && codeData.resourceId) {
                const childRes = await resourcesRepository.find(codeData.resourceId, cCode);
                // Filtra para exibir APENAS os itens com status "Agregado"
                if (childRes && childRes.status?.toLowerCase() === 'agregado') {
                  results.push({ code: cCode, resource: childRes });
                }
              }
            } catch (err) {
              console.log('Erro ao buscar filho manipulado', cCode);
            }
          }
          setAggregatedChildren(results);
        } finally {
          setLoadingAggregation(false);
        }
      };
      
      fetchChildren();
    }
  }, [qrCode?.value]);

  const getInputValue = (
    questionId: string,
    type: 'select' | 'input'
  ): string | { value: string; label: string } => {
    const found = forum.find((q) => q.questionId === questionId);
    if (!found) return '';
    if (type === 'input') return found.answerText || '';
    return { value: found.questionId, label: found.answerText };
  };

  const submit = async () => {
    if (!forum.length) {
      toast.error('Preencha ao menos uma resposta antes de enviar.');
      return;
    }

    setIsSubmitting(true);
    try {
      await answerRepository.createQuestions(forum);

      if (resource?.id && latLng) {
        await interactionRepository.saveInteraction({
          resourceId: resource.id,
          code: qrCode.value,
          type: 'interact',
          latitude: latLng.lat,
          longitude: latLng.lng,
          status: 'Envasado',
        });
      }

      setForum([]);
      setIsSubmitted(true);
      toast.success('Respostas enviadas com sucesso!');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao enviar respostas.';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadingResources) {
    return <Loading text="Carregando informações do produto..." />;
  }

  return (
    <Body>
      {/* ── Alerta de Produto Extraviado ── */}
      {resource?.status?.toLowerCase() === 'extraviado' && (
        <ExtraviadoAlert>
          <MdWarning size={24} />
          <p>Atenção consumidor: este item foi reportado como EXTRAVIADO no sistema.</p>
        </ExtraviadoAlert>
      )}

      {/* ── Card principal do produto ── */}
      <ProductDetailsContainer>
        <CodeBox>
          <CodeLabel>{qrCode.value}</CodeLabel>
        </CodeBox>

        <ProductLayout>
          <ProductInfoContainer>
            {resource?.name && <ProductName>{resource.name}</ProductName>}
            {resource?.description && (
              <ProductDescription>{resource.description}</ProductDescription>
            )}
          </ProductInfoContainer>
        </ProductLayout>

        {/* Status e data */}
        {(resource?.status || resource?.statusDate) && (
          <>
            <Divider />
            <StatsContainer>
              {resource?.status && (
                <StatItem>
                  <StatLabel>Status</StatLabel>
                  <StatusBadge status={resource.status} />
                </StatItem>
              )}
              {resource?.statusDate && (
                <StatItem>
                  <StatLabel>Data do Status</StatLabel>
                  <StatValue>
                    {format(new Date(resource.statusDate), 'dd/MM/yyyy HH:mm')}
                  </StatValue>
                </StatItem>
              )}
            </StatsContainer>
          </>
        )}

        {/* Campos dinâmicos */}
        {resource?.fields && resource.fields.length > 0 && (
          <>
            <Divider />
            <StatsContainer>
              {resource.fields.map((item, idx) => (
                <StatItem key={idx}>
                  <StatLabel>{item.resourceField.name}</StatLabel>
                  <StatValue>{item.value}</StatValue>
                </StatItem>
              ))}
            </StatsContainer>
          </>
        )}
      </ProductDetailsContainer>

      {/* ── Itens Agregados (Filhos) ── */}
      {(aggregatedChildren.length > 0 || loadingAggregation) && (
        <AggregatedItemsCard>
          <AggregatedTitle>
            <MdLayers size={20} />
            Itens Agregados ({loadingAggregation ? '...' : aggregatedChildren.length})
          </AggregatedTitle>
          <AggregatedList>
            {loadingAggregation ? (
              <AggregatedItem>
                <div className="info">
                  <strong>Carregando itens vinculados...</strong>
                </div>
              </AggregatedItem>
            ) : (
              aggregatedChildren.map((child) => (
                <AggregatedItem key={child.code}>
                  <div className="info">
                    <strong>{child.resource.name || 'Item Vinculado'}</strong>
                    <span>Cód: {child.code}</span>
                  </div>
                  <div className="date">
                    {child.resource.statusDate ? format(new Date(child.resource.statusDate), 'dd/MM/yyyy HH:mm') : 'Data não disponível'}
                  </div>
                </AggregatedItem>
              ))
            )}
          </AggregatedList>
        </AggregatedItemsCard>
      )}

      {/* ── Card de perguntas / interação ── */}
      <QuestionsCard>
        {resource?.questions?.length && !isSubmitted ? (
          <>
            <QuestionsTitle>Responda sobre o produto</QuestionsTitle>
            <QuestionScroll>
              {resource.questions.map((question) =>
                question.options.length ? (
                  <FieldContainer key={question.id}>
                    <Text>{question.questionText}</Text>
                    <Select
                      name={question.id}
                      id={question.id}
                      options={question.options.map((opt) => ({
                        value: opt.id,
                        label: opt.optionText,
                      }))}
                      value={getInputValue(question.id, 'select') as string}
                      onChange={(val: any) => changeValue(question.id, val.label)}
                      placeholder="Selecione uma opção"
                      noOptionsMessage={() => 'Nenhuma opção encontrada.'}
                    />
                  </FieldContainer>
                ) : (
                  <FieldContainer key={question.id}>
                    <Text>{question.questionText}</Text>
                    <Input
                      type="text"
                      name="answer"
                      value={getInputValue(question.id, 'input') as string}
                      onChange={(e) => changeValue(question.id, e.target.value)}
                      placeholder="Digite sua resposta"
                    />
                  </FieldContainer>
                )
              )}
            </QuestionScroll>

            <ButtonContainer>
              <div>
                <Button
                  type="submit"
                  onClick={submit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Enviando...' : 'Enviar'}
                </Button>
              </div>
            </ButtonContainer>
          </>
        ) : isSubmitted ? (
          <SubmittedMessage>
            <MdCheckCircle size={48} color="currentColor" />
            <h4>Respostas enviadas!</h4>
            <p>Obrigado pela sua participação.</p>
          </SubmittedMessage>
        ) : (
          <NoInteractionMessage isWarning={resource?.status?.toLowerCase() === 'extraviado'}>
            {resource?.status?.toLowerCase() === 'extraviado' ? (
              <MdWarning size={20} />
            ) : (
              <MdInfo size={20} />
            )}
            <p>{resource?.canInteractMessage || 'Sem interações disponíveis no momento.'}</p>
          </NoInteractionMessage>
        )}
      </QuestionsCard>
    </Body>
  );
};

export default ProductByCode;
